import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import Home from './components/Home';
import StorePage from './components/Shop';
import GuidesPage from './components/guides';
import ChipGuidePage from './components/chipg';
import MYOGuidePage from './components/myo';
import MystveilUpgradesPage from './components/upgradesG';
import MystveilUpgradesDevsPage from './components/upgradesDevs';
import NewbieGuidePage from './components/newbiG';
import MystVeilRules from './components/rules';
import NewsPage from './components/news';
import ComingSoon from './components/CS';
import EventCalendar from './components/EventCalendar';
import HiddenShop from './components/HiddenShop';
import MystveilProfile from './components/MystveilProfile';
import VeilkinProfiles from './components/VeilkinProfiles';
import UpgradesShop from './components/UpgradesShop';
import ImageSubmission from './components/ImageSubmission';
import GalleryBrowser from './components/GalleryBrowser';
import AdminModeration from './components/AdminModeration';
import MystveilAuth from './components/MystveilAuth';
import Forum from './components/Forum';
import EmailVerification from './components/EmailVerification';
import CharacterDetail from './components/CharacterDetail';

  const pages = {
    '/': Home,
    '/shop': HiddenShop,
    '/merchshop': StorePage,
    '/news': NewsPage,
    '/guide': GuidesPage,
    '/guide/MYO': MYOGuidePage,
    '/upgrades': MystveilUpgradesPage,
    '/upgradesDevs': MystveilUpgradesDevsPage,
    '/guide/Chip': ChipGuidePage,
    '/guide/Newbie': NewbieGuidePage,
     '/rules': MystVeilRules,
     '/coming-soon': ComingSoon,
     '/events': EventCalendar,
    // Mystveil Pages
    '/profile': MystveilProfile,
    '/characters': VeilkinProfiles,
    '/upgrades/:characterId': UpgradesShop,
    '/submit': ImageSubmission,
    '/gallery': GalleryBrowser,
    '/gallery/:galleryType': GalleryBrowser,
    '/gallery/player/:username': GalleryBrowser,
    '/gallery/character/:characterId': GalleryBrowser,
    '/global-gallery': GalleryBrowser,
    '/admin/moderation': AdminModeration,
    '/forum': Forum,
    // Auth Pages
    '/auth/login': MystveilAuth,
    '/auth/register': MystveilAuth,
    '/auth/verify': EmailVerification,
  };

const TransitionWrapper = () => {
  const location = useLocation();
  const videoRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showDarkFlash, setShowDarkFlash] = useState(false);
  const [content, setContent] = useState(<Home />);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(false);
  const prevPathnameRef = useRef(location.pathname);

  const videoSrc = 'https://cdn.4fingerstudios.com/';
  const transitionPause = 600;
  const transitionDuration = 1000;
  const flashDuration = 350;

  useEffect(() => {
    mountedRef.current = true;
    console.log('Component mounted, initial path:', location.pathname);
    updateContent(location.pathname);
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      console.log('Component unmounted');
    };
  }, []);

  const updateContent = (pathname) => {
    // Handle parameterized routes
    let ActivePage = pages[pathname];
    
    if (!ActivePage) {
      // Check for parameterized routes
      if (pathname.startsWith('/upgrades/')) {
        const characterId = pathname.split('/')[2];
        ActivePage = () => <UpgradesShop characterId={characterId} />;
      } else if (pathname.startsWith('/gallery/player/')) {
        const username = pathname.split('/')[3];
        ActivePage = () => <GalleryBrowser username={username} />;
      } else if (pathname.startsWith('/gallery/character/')) {
        const characterId = pathname.split('/')[3];
        ActivePage = () => <GalleryBrowser characterId={characterId} />;
      } else if (pathname.startsWith('/gallery/') && pathname.split('/').length === 3) {
        const galleryType = pathname.split('/')[2];
        ActivePage = () => <GalleryBrowser galleryType={galleryType} />;
      } else if (pathname.startsWith('/characters/') && pathname.split('/').length === 3) {
        const characterId = pathname.split('/')[2];
        ActivePage = () => <CharacterDetail characterId={characterId} />;
      }
    }
    
    ActivePage = ActivePage || Home;
    setContent(<ActivePage />);
  };

  useEffect(() => {
    const handleTransition = async () => {
      console.log('handleTransition called, current path:', location.pathname, 'prev path:', prevPathnameRef.current);

      if (!mountedRef.current) {
        console.log('Not mounted or same path, skipping transition');
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        console.log('Cleared previous timeout');
      }

      console.log('Starting transition');
      setIsTransitioning(true);
      setShowDarkFlash(true);

      const video = videoRef.current;
      if (!video) {
        console.error('Video ref not found');
        updateContent(location.pathname);
        setIsTransitioning(false);
        setShowDarkFlash(false);
        return;
      }

      try {
        console.log('Playing video');
        video.currentTime = 0;
        await video.play();

        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            console.log('Halfway mark: switching content');
            updateContent(location.pathname);
            timeoutRef.current = setTimeout(() => {
              if (mountedRef.current) {
                console.log('Hiding dark flash');
                setShowDarkFlash(false);
              }
            }, flashDuration);
          }
        }, transitionPause);

        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            console.log('Transition complete');
            setIsTransitioning(false);
            prevPathnameRef.current = location.pathname;
          }
        }, transitionDuration);
      } catch (error) {
        console.error('Transition error:', error);
        updateContent(location.pathname);
        setIsTransitioning(false);
        setShowDarkFlash(false);
      }
    };

    handleTransition();
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen">
      <div className="relative z-0">{content}</div>
      <div
        className={`fixed inset-0 w-screen h-screen bg-gray-900 pointer-events-none z-40 transition-opacity duration-200 ${
          showDarkFlash ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <video
        ref={videoRef}
        className={`fixed inset-0 w-screen h-screen object-cover mix-blend-screen pointer-events-none z-50 ${
          isTransitioning ? 'block' : 'hidden'
        }`}
        style={{ filter: 'contrast(150%)' }}
        src={videoSrc}
        playsInline
        preload="auto"
        muted
        onError={(e) => console.error('Video error:', e)}
      />
    </div>
  );
};

const App = () => {
  useEffect(() => {
    const preventZoom = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-')) {
        e.preventDefault();
      }
    };
    const preventWheelZoom = (e) => {
      if (e.ctrlKey && e.type === 'wheel') e.preventDefault();
    };
    document.addEventListener('keydown', preventZoom);
    document.addEventListener('wheel', preventWheelZoom, { passive: false });
    return () => {
      document.removeEventListener('keydown', preventZoom);
      document.removeEventListener('wheel', preventWheelZoom);
    };
  }, []);

  return (
    <Router>
      <TransitionWrapper />
    </Router>
  );
};

export default App;